import { create } from "zustand";
import { STORAGE_KEYS } from "../constants/storage";

export interface WatchlistItem {
  name: string;
  code: string;
  market: string;
  addedAt: string;
  score?: number;
  grade?: string;
  prevScore?: number;
  baseScore?: number;
  scoreUpdatedAt?: string;
}

const STORAGE_KEY = STORAGE_KEYS.watchlist;
const MAX_ITEMS = 30;

function load(): WatchlistItem[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

function save(items: WatchlistItem[]) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
}

interface WatchlistState {
  watchlist: WatchlistItem[];
  addStock: (item: Omit<WatchlistItem, "addedAt">) => void;
  removeStock: (code: string) => void;
  isWatched: (code: string) => boolean;
  updateScore: (code: string, score: number, grade?: string) => void;
}

export const useWatchlistStore = create<WatchlistState>((set, get) => ({
  watchlist: load(),
  addStock: (item) =>
    set((state) => {
      if (state.watchlist.some((w) => w.code === item.code)) return state;
      const newItem: WatchlistItem = {
        ...item,
        addedAt: new Date().toISOString(),
        // 추가 시점 점수를 기준점으로 고정 — 이후 updateScore에서 유지됨
        baseScore: item.score != null ? item.score : undefined,
      };
      const next = [newItem, ...state.watchlist].slice(0, MAX_ITEMS);
      save(next);
      return { watchlist: next };
    }),
  removeStock: (code) =>
    set((state) => {
      const next = state.watchlist.filter((w) => w.code !== code);
      save(next);
      return { watchlist: next };
    }),
  isWatched: (code) => get().watchlist.some((w) => w.code === code),
  updateScore: (code, score, grade) =>
    set((state) => {
      const idx = state.watchlist.findIndex((w) => w.code === code);
      if (idx === -1) return state; // 워치 안 한 종목 — 무시
      const existing = state.watchlist[idx];
      // baseScore: 이미 설정돼 있으면 유지, 없으면 기존 score(또는 이번 score)로 첫 설정
      // — 기존 localStorage 항목(baseScore 없음)에서 첫 updateScore 시 자동 채워짐
      const baseScore =
        existing.baseScore != null ? existing.baseScore : (existing.score ?? score);
      const updated: WatchlistItem = {
        ...existing,
        prevScore: existing.score !== undefined ? existing.score : undefined,
        score,
        grade: grade ?? existing.grade,
        baseScore,
        scoreUpdatedAt: new Date().toISOString(),
      };
      const next = [...state.watchlist];
      next[idx] = updated;
      save(next);
      return { watchlist: next };
    }),
}));
